import { useMutation } from '@apollo/client';
import type { TaxonomyApi } from '@seek/sbiz-shared-types';
import { getIndustryGroups } from '@seek/sbiz-taxonomies';
import { useState } from 'react';

import type { SelectBoxIndustryGrouping } from 'src/components/IndustryClassificationsField/IndustryClassificationsField';

import { UpdatePreferencesIndustryDocument } from '../../../../generated/graphql';

export const usePreferencesIndustryField = (selectedIndustries: string[]) => {
  const initialIndustryGroups = getIndustryGroups(); // mock this
  const [value, setValue] = useState<SelectBoxIndustryGrouping[]>(
    initialIndustryGroups.map((g) =>
      mapIndustryGroupToFieldValue(g, selectedIndustries),
    ),
  );
  const selected = getSelectedIndustries(value);

  const [updatePreferencesIndustry] = useMutation(
    // mock this
    UpdatePreferencesIndustryDocument,
  );

  const handleChange = (changeValue: SelectBoxIndustryGrouping[]) => {
    const newSelectedIndustries = getSelectedIndustries(changeValue);
    updatePreferencesIndustry({ variables: { input: newSelectedIndustries } });
    setValue(changeValue);
  };

  const handleRemoveSingleIndustry = (targetIndustry: string) => {
    const newIndustryGroupings = toggleSingleIndustry(targetIndustry, value);
    const newSelectedIndustries = getSelectedIndustries(newIndustryGroupings);
    updatePreferencesIndustry({ variables: { input: newSelectedIndustries } });
    setValue(newIndustryGroupings);
  };

  return {
    value,
    selected,
    onChange: handleChange,
    onRemoveSingleIndustry: handleRemoveSingleIndustry,
  };
};

// ============================================================================
// Helpers
// ============================================================================

const mapIndustryGroupToFieldValue = (
  industryGroup: TaxonomyApi.IndustryGroup,
  selectedIndustries: string[],
): SelectBoxIndustryGrouping => ({
  title: industryGroup.title,
  selected: industryGroup.industries.some((industry) => {
    if (selectedIndustries.some((s) => industry.title.includes(s))) {
      return true;
    }
    return false;
  }),

  industries: industryGroup.industries.map((industry) => {
    if (selectedIndustries.some((s) => industry.title.includes(s))) {
      return {
        ...industry,
        selected: true,
      };
    }
    return {
      ...industry,
      selected: false,
    };
  }),
  urlKey: industryGroup.urlKey,
});

const getSelectedIndustries = (industries: SelectBoxIndustryGrouping[]) =>
  industries.flatMap((grouping) => {
    const isMacroGroupSelected =
      grouping.selected && grouping.industries.every((i) => !i.selected);
    const isMicroGroupSelected = !isMacroGroupSelected;

    if (isMacroGroupSelected) {
      return grouping.industries.map((i) => i.title);
    } else if (isMicroGroupSelected) {
      return grouping.industries.filter((i) => i.selected).map((i) => i.title);
    }
    return [];
  });

export const toggleSingleIndustry = (
  targetIndustry: string,
  industryGroupings: SelectBoxIndustryGrouping[],
): SelectBoxIndustryGrouping[] =>
  industryGroupings.map((grouping) => {
    if (grouping.industries.some((i) => i.title.includes(targetIndustry))) {
      if (grouping.industries.every((i) => !i.selected)) {
        return {
          ...grouping,
          industries: grouping.industries.map((i) => {
            if (i.title.includes(targetIndustry)) {
              return { ...i, selected: false };
            }
            return {
              ...i,
              selected: true,
            };
          }),
        };
      }
      return {
        ...grouping,
        selected: grouping.industries.filter((i) => i.selected).length > 1,
        industries: grouping.industries.map((i) => {
          if (i.title.includes(targetIndustry)) {
            return { ...i, selected: false };
          }
          return {
            ...i,
          };
        }),
      };
    }
    return { ...grouping };
  });
